/**
 * Poker betting engine (Texas Hold'em first, variants pluggable via {@link POKER_VARIANTS}).
 *
 * Implemented:
 *  - Dealer button, small/big blinds (+ optional ante), heads-up rules (button posts SB, acts first pre-flop).
 *  - Streets: pre-flop, flop (3), turn (1), river (1) with burn cards.
 *  - Actions: fold, check, call, bet/raise (raise-to amounts), all-in.
 *  - No-limit min-raise rules incl. incomplete all-in raises that do not re-open betting.
 *  - Pot-limit maximums (PLO).
 *  - Side pots for any number of all-ins; uncalled bets returned; split pots with odd chips to the first
 *    winner left of the button.
 *  - Showdown with best-hand evaluation, automatic board run-out when no further betting is possible.
 *  - Cash mode (sit out on 0 chips) and tournament mode (blind levels by hand count, eliminations, finish order).
 *  - Optional rake on contested pots that saw a flop ("no flop, no drop"), with cap.
 */
import { buildDeck, type CardId } from '../../core/cards.js';
import { checkEnum, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import { nextSeatWhere, seatsWhere, splitAmount } from '../../core/seats.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
  type Standing,
} from '../../core/types.js';
import { categoryOf, type PokerCategory } from './evaluator.js';
import { buildPots, type Contribution, type Pot } from './pots.js';
import { POKER_VARIANTS, type PokerVariantId } from './variants.js';

export interface BlindLevel {
  sb: number;
  bb: number;
  ante: number;
}

export interface PokerConfig {
  variant: PokerVariantId;
  mode: 'cash' | 'tournament';
  maxPlayers: number;
  minPlayers: number;
  smallBlind: number;
  bigBlind: number;
  ante: number;
  /** Tournament only: blind schedule; level advances every `handsPerLevel` hands. */
  blindLevels: BlindLevel[];
  handsPerLevel: number;
  rakePercent: number;
  rakeCap: number;
  handEndDelayMs: number;
  startDelayMs: number;
}

export type PokerStatus = 'WAITING' | 'ACTIVE' | 'FOLDED' | 'ALLIN' | 'SITTING_OUT' | 'BUSTED';
export type PokerPhase = 'WAITING' | 'PREFLOP' | 'FLOP' | 'TURN' | 'RIVER' | 'HAND_END' | 'FINISHED';

export interface PokerPlayer {
  playerId: string;
  chips: number;
  status: PokerStatus;
  hole: CardId[];
  /** Chips committed on the current street. */
  bet: number;
  /** Chips committed this hand (all streets + ante). */
  total: number;
  acted: boolean;
  raiseCountSeen: number;
  chipsAtHandStart: number;
}

export interface PokerPotResult {
  amount: number;
  rake: number;
  winners: number[];
  payouts: Record<number, number>;
  eligible: number[];
  returned: boolean;
}

export interface PokerHandResult {
  handNo: number;
  board: CardId[];
  pots: PokerPotResult[];
  revealed: Record<number, CardId[]>;
  categories: Record<number, PokerCategory>;
  uncontested: boolean;
  netChange: Record<number, number>;
}

export interface Elimination {
  seat: number;
  playerId: string;
  place: number;
  handNo: number;
}

export interface PokerState {
  config: PokerConfig;
  seats: (PokerPlayer | null)[];
  phase: PokerPhase;
  handNo: number;
  button: number;
  sbSeat: number;
  bbSeat: number;
  turn: number;
  board: CardId[];
  /** Remaining deck — server-only, never exposed in views. */
  deck: CardId[];
  currentBet: number;
  minRaise: number;
  fullRaiseCount: number;
  departed: Contribution[];
  lastResult: PokerHandResult | null;
  eliminations: Elimination[];
  rakeTotal: number;
  lastAction: { seat: number; action: string; amount: number } | null;
}

export type PokerAction =
  | { type: 'fold' }
  | { type: 'check' }
  | { type: 'call' }
  | { type: 'raise'; to: number }
  | { type: 'allin' };

export interface PokerLegal {
  canFold: boolean;
  canCheck: boolean;
  /** Chips needed to call (may be all-in for less). */
  call: number | null;
  /** Raise-to bounds (total street bet). null when raising isn't allowed. */
  minRaiseTo: number | null;
  maxRaiseTo: number | null;
  /** Raise-to amount if going all-in is a legal action. */
  allInTo: number | null;
  toCall: number;
}

export interface PokerPlayerView {
  seat: number;
  playerId: string;
  chips: number;
  status: PokerStatus;
  bet: number;
  total: number;
  hasCards: boolean;
}

export interface PokerView {
  gameType: 'POKER';
  config: PokerConfig;
  seat: number | null;
  phase: PokerPhase;
  handNo: number;
  button: number;
  sbSeat: number;
  bbSeat: number;
  turn: number;
  board: CardId[];
  hand: CardId[];
  players: (PokerPlayerView | null)[];
  pot: number;
  currentBet: number;
  minRaise: number;
  blinds: BlindLevel;
  lastResult: PokerHandResult | null;
  eliminations: Elimination[];
  lastAction: PokerState['lastAction'];
}

const DEFAULTS: PokerConfig = {
  variant: 'NLHE',
  mode: 'cash',
  maxPlayers: 6,
  minPlayers: 2,
  smallBlind: 50,
  bigBlind: 100,
  ante: 0,
  blindLevels: [],
  handsPerLevel: 10,
  rakePercent: 0,
  rakeCap: 0,
  handEndDelayMs: 5000,
  startDelayMs: 2500,
};

const live = (p: PokerPlayer) => p.status === 'ACTIVE' || p.status === 'ALLIN';
const canAct = (p: PokerPlayer) => p.status === 'ACTIVE' && p.chips > 0;

export function currentBlinds(s: Pick<PokerState, 'config' | 'handNo'>): BlindLevel {
  const c = s.config;
  if (c.mode === 'tournament' && c.blindLevels.length > 0) {
    const idx = Math.min(c.blindLevels.length - 1, Math.floor(Math.max(0, s.handNo - 1) / c.handsPerLevel));
    return c.blindLevels[idx]!;
  }
  return { sb: c.smallBlind, bb: c.bigBlind, ante: c.ante };
}

function eligibleForHand(p: PokerPlayer): boolean {
  return p.chips > 0 && p.status !== 'SITTING_OUT' && p.status !== 'BUSTED';
}

function commit(s: PokerState, seat: number, amount: number): number {
  const p = s.seats[seat]!;
  const a = Math.min(amount, p.chips);
  p.chips -= a;
  p.bet += a;
  p.total += a;
  if (p.chips === 0 && p.status === 'ACTIVE') p.status = 'ALLIN';
  return a;
}

function potTotal(s: PokerState): number {
  let t = s.departed.reduce((a, c) => a + c.amount, 0);
  for (const p of s.seats) if (p) t += p.total;
  return t;
}

function dealBoard(s: PokerState, n: number, events: EngineEvent[]): void {
  s.deck.shift(); // burn
  const cards = s.deck.splice(0, n);
  s.board.push(...cards);
  events.push({ type: 'board', cards, board: [...s.board] });
}

function startHand(s: PokerState, rng: Rng, events: EngineEvent[]): void {
  const variant = POKER_VARIANTS[s.config.variant];
  s.handNo++;
  const blinds = currentBlinds(s);
  s.deck = shuffle(buildDeck(), rng);
  s.board = [];
  s.departed = [];
  s.lastResult = null;
  s.lastAction = null;
  const players = seatsWhere(s.seats, eligibleForHand);
  for (const seat of seatsWhere(s.seats, () => true)) {
    const p = s.seats[seat]!;
    p.bet = 0;
    p.total = 0;
    p.acted = false;
    p.raiseCountSeen = 0;
    p.hole = [];
    p.chipsAtHandStart = p.chips;
    if (players.includes(seat)) p.status = 'ACTIVE';
    else if (p.status !== 'BUSTED' && p.status !== 'SITTING_OUT') p.status = 'WAITING';
  }
  s.button = nextSeatWhere(s.seats, s.button, (p) => p.status === 'ACTIVE')!;
  const headsUp = players.length === 2;
  s.sbSeat = headsUp ? s.button : nextSeatWhere(s.seats, s.button, (p) => p.status === 'ACTIVE')!;
  s.bbSeat = nextSeatWhere(s.seats, s.sbSeat, (p) => p.status === 'ACTIVE')!;
  for (const seat of players) s.seats[seat]!.hole = s.deck.splice(0, variant.holeCards);
  if (blinds.ante > 0) {
    for (const seat of players) {
      commit(s, seat, blinds.ante);
      s.seats[seat]!.bet = 0; // antes are dead money, not part of the street bet
    }
  }
  const sb = commit(s, s.sbSeat, blinds.sb);
  const bb = commit(s, s.bbSeat, blinds.bb);
  s.currentBet = blinds.bb;
  s.minRaise = blinds.bb;
  s.fullRaiseCount = 0;
  s.phase = 'PREFLOP';
  events.push({
    type: 'handStart',
    handNo: s.handNo,
    button: s.button,
    sbSeat: s.sbSeat,
    bbSeat: s.bbSeat,
    blinds,
    posted: { [s.sbSeat]: sb, [s.bbSeat]: bb },
    players,
  });
  const first = nextSeatWhere(s.seats, s.bbSeat, canAct);
  s.turn = first ?? s.bbSeat;
  progressIfRoundDone(s, events);
}

function needsAction(s: PokerState, p: PokerPlayer): boolean {
  return canAct(p) && (!p.acted || p.bet < s.currentBet);
}

function roundComplete(s: PokerState): boolean {
  return seatsWhere(s.seats, (p) => needsAction(s, p)).length === 0;
}

function progressIfRoundDone(s: PokerState, events: EngineEvent[]): void {
  const liveSeats = seatsWhere(s.seats, live);
  if (liveSeats.length === 1) {
    finishHand(s, events);
    return;
  }
  if (!roundComplete(s)) {
    const cur = s.seats[s.turn];
    s.turn = cur && needsAction(s, cur) ? s.turn : nextSeatWhere(s.seats, s.turn, (p) => needsAction(s, p))!;
    return;
  }
  // Street over.
  for (const p of s.seats) if (p) {
    p.bet = 0;
    p.acted = false;
  }
  s.currentBet = 0;
  s.minRaise = currentBlinds(s).bb;
  s.fullRaiseCount++;
  const actors = seatsWhere(s.seats, canAct);
  const runOut = actors.length <= 1;
  const next = (): boolean => {
    if (s.phase === 'PREFLOP') {
      s.phase = 'FLOP';
      dealBoard(s, 3, events);
    } else if (s.phase === 'FLOP') {
      s.phase = 'TURN';
      dealBoard(s, 1, events);
    } else if (s.phase === 'TURN') {
      s.phase = 'RIVER';
      dealBoard(s, 1, events);
    } else return false;
    return true;
  };
  if (runOut) {
    while (next()) {
      /* deal remaining streets */
    }
    finishHand(s, events);
    return;
  }
  if (!next()) {
    finishHand(s, events);
    return;
  }
  events.push({ type: 'street', phase: s.phase });
  s.turn = nextSeatWhere(s.seats, s.button, canAct)!;
}

function finishHand(s: PokerState, events: EngineEvent[]): void {
  const variant = POKER_VARIANTS[s.config.variant];
  const contribs: Contribution[] = [...s.departed];
  for (const seat of seatsWhere(s.seats, () => true)) {
    const p = s.seats[seat]!;
    if (p.total > 0 || live(p)) contribs.push({ seat, amount: p.total, folded: !live(p) });
  }
  const liveSeats = seatsWhere(s.seats, live);
  const uncontested = liveSeats.length === 1;
  const values = new Map<number, number>();
  if (!uncontested) for (const seat of liveSeats) values.set(seat, variant.evaluate(s.seats[seat]!.hole, s.board));
  const pots: Pot[] = buildPots(contribs);
  const results: PokerPotResult[] = [];
  let rakeLeft = s.config.rakeCap > 0 ? s.config.rakeCap : Infinity;
  const sawFlop = s.board.length >= 3;
  // Order for odd chips: first seat left of the button.
  const orderFromButton = (seats: number[]) =>
    [...seats].sort((a, b) => ((a - s.button - 1 + 100 * s.seats.length) % s.seats.length) - ((b - s.button - 1 + 100 * s.seats.length) % s.seats.length));
  for (const pot of pots) {
    const eligible = pot.eligible.filter((x) => s.seats[x] && live(s.seats[x]!));
    if (eligible.length === 0) continue;
    const returned = eligible.length === 1;
    let winners: number[];
    if (returned || uncontested) winners = [eligible[0]!];
    else {
      const best = Math.max(...eligible.map((x) => values.get(x)!));
      winners = orderFromButton(eligible.filter((x) => values.get(x) === best));
    }
    let rake = 0;
    if (!returned && !uncontested && sawFlop && s.config.rakePercent > 0) {
      rake = Math.min(rakeLeft, Math.floor((pot.amount * s.config.rakePercent) / 100));
      rakeLeft -= rake;
    }
    const shares = splitAmount(pot.amount - rake, winners);
    const payouts: Record<number, number> = {};
    for (const [seat, amt] of shares) {
      s.seats[seat]!.chips += amt;
      payouts[seat] = amt;
    }
    s.rakeTotal += rake;
    results.push({ amount: pot.amount, rake, winners, payouts, eligible, returned });
  }
  const revealed: Record<number, CardId[]> = {};
  const categories: Record<number, PokerCategory> = {};
  if (!uncontested) {
    for (const seat of liveSeats) {
      revealed[seat] = [...s.seats[seat]!.hole];
      categories[seat] = categoryOf(values.get(seat)!);
    }
  }
  const netChange: Record<number, number> = {};
  for (const seat of seatsWhere(s.seats, () => true)) {
    const p = s.seats[seat]!;
    if (p.status !== 'WAITING' && p.status !== 'SITTING_OUT' && p.status !== 'BUSTED') netChange[seat] = p.chips - p.chipsAtHandStart;
  }
  s.lastResult = { handNo: s.handNo, board: [...s.board], pots: results, revealed, categories, uncontested, netChange };
  events.push({ type: 'handEnd', ...s.lastResult });
  s.phase = 'HAND_END';
  s.lastAction = null;

  // Busts / eliminations.
  const busted = seatsWhere(s.seats, (p) => p.chips === 0 && (p.status === 'FOLDED' || p.status === 'ALLIN' || p.status === 'ACTIVE'));
  if (s.config.mode === 'tournament') {
    const remaining = seatsWhere(s.seats, (p) => p.status !== 'BUSTED').length;
    // Bigger starting stack finishes higher when several bust in the same hand.
    const ordered = [...busted].sort((a, b) => s.seats[b]!.chipsAtHandStart - s.seats[a]!.chipsAtHandStart);
    ordered.forEach((seat, i) => {
      const p = s.seats[seat]!;
      p.status = 'BUSTED';
      const place = remaining - busted.length + 1 + i;
      s.eliminations.push({ seat, playerId: p.playerId, place, handNo: s.handNo });
      events.push({ type: 'eliminated', seat, place });
    });
    const left = seatsWhere(s.seats, (p) => p.status !== 'BUSTED');
    if (left.length === 1) {
      s.eliminations.push({ seat: left[0]!, playerId: s.seats[left[0]!]!.playerId, place: 1, handNo: s.handNo });
      s.phase = 'FINISHED';
      events.push({ type: 'tournamentEnd', winner: left[0] });
    }
  } else {
    for (const seat of busted) s.seats[seat]!.status = 'SITTING_OUT';
  }
}

function legalFor(s: PokerState, seat: number): PokerLegal {
  const none: PokerLegal = { canFold: false, canCheck: false, call: null, minRaiseTo: null, maxRaiseTo: null, allInTo: null, toCall: 0 };
  const p = s.seats[seat];
  if (!p || s.turn !== seat || !canAct(p) || !['PREFLOP', 'FLOP', 'TURN', 'RIVER'].includes(s.phase)) return none;
  const toCall = Math.max(0, s.currentBet - p.bet);
  const legal: PokerLegal = { ...none, canFold: true, toCall, canCheck: toCall === 0 };
  if (toCall > 0) legal.call = Math.min(toCall, p.chips);
  const othersCanAct = seatsWhere(s.seats, (q, i) => i !== seat && canAct(q)).length > 0;
  const mayRaise = (!p.acted || p.raiseCountSeen < s.fullRaiseCount) && othersCanAct && p.chips > toCall;
  if (!mayRaise) return legal;
  const allInTo = p.bet + p.chips;
  let maxTo = allInTo;
  if (POKER_VARIANTS[s.config.variant].bettingStructure === 'pot-limit') {
    const potLimitTo = s.currentBet + potTotal(s) + toCall;
    maxTo = Math.min(allInTo, potLimitTo);
  }
  const minTo = s.currentBet + s.minRaise;
  legal.minRaiseTo = Math.min(minTo, maxTo);
  legal.maxRaiseTo = maxTo;
  legal.allInTo = allInTo <= maxTo ? allInTo : null;
  return legal;
}

function doRaise(s: PokerState, seat: number, to: number, events: EngineEvent[]): void {
  const p = s.seats[seat]!;
  const raiseSize = to - s.currentBet;
  commit(s, seat, to - p.bet);
  if (raiseSize >= s.minRaise) {
    s.minRaise = raiseSize;
    s.fullRaiseCount++;
  }
  const wasBet = s.currentBet === 0;
  s.currentBet = to;
  p.acted = true;
  p.raiseCountSeen = s.fullRaiseCount;
  const kind = p.status === 'ALLIN' ? 'allin' : wasBet ? 'bet' : 'raise';
  s.lastAction = { seat, action: kind, amount: to };
  events.push({ type: 'action', seat, action: kind, to, chips: p.chips, pot: potTotal(s) });
}

export const pokerEngine: GameEngine<PokerConfig, PokerState, PokerAction, PokerView, PokerLegal> = {
  gameType: 'POKER',
  kind: 'session',

  defaultConfig: () => structuredClone(DEFAULTS),

  normalizeConfig(input) {
    const c = mergeKnown(structuredClone(DEFAULTS), input);
    checkEnum('variant', c.variant, Object.keys(POKER_VARIANTS) as PokerVariantId[]);
    checkEnum('mode', c.mode, ['cash', 'tournament'] as const);
    checkInt('maxPlayers', c.maxPlayers, 2, 10);
    checkInt('minPlayers', c.minPlayers, 2, c.maxPlayers);
    checkInt('smallBlind', c.smallBlind, 1, 1e9);
    checkInt('bigBlind', c.bigBlind, c.smallBlind, 1e9);
    checkInt('ante', c.ante, 0, 1e9);
    checkInt('handsPerLevel', c.handsPerLevel, 1, 1000);
    checkInt('rakePercent', c.rakePercent, 0, 10);
    checkInt('rakeCap', c.rakeCap, 0, 1e9);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    if (!Array.isArray(c.blindLevels)) throw new EngineError('INVALID_CONFIG', 'blindLevels must be an array');
    c.blindLevels = c.blindLevels.map((l, i) => ({
      sb: checkInt(`blindLevels[${i}].sb`, l?.sb, 1, 1e9),
      bb: checkInt(`blindLevels[${i}].bb`, l?.bb, l?.sb ?? 1, 1e9),
      ante: checkInt(`blindLevels[${i}].ante`, l?.ante ?? 0, 0, 1e9),
    }));
    if (POKER_VARIANTS[c.variant].holeCards * c.maxPlayers + 8 > 52) throw new EngineError('INVALID_CONFIG', 'Too many players for variant');
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: PokerState = {
      config,
      seats: Array<PokerPlayer | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      button: rng.int(config.maxPlayers),
      sbSeat: -1,
      bbSeat: -1,
      turn: -1,
      board: [],
      deck: [],
      currentBet: 0,
      minRaise: config.bigBlind,
      fullRaiseCount: 0,
      departed: [],
      lastResult: null,
      eliminations: [],
      rakeTotal: 0,
      lastAction: null,
    };
    for (const setup of seats) pokerEngine.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips > 0, 'INVALID_AMOUNT', 'Buy-in must be positive');
    assertEngine(!(s.config.mode === 'tournament' && s.handNo > 0), 'INVALID_ACTION', 'Tournament already started');
    s.seats[setup.seat] = {
      playerId: setup.playerId,
      chips,
      status: 'WAITING',
      hole: [],
      bet: 0,
      total: 0,
      acted: false,
      raiseCountSeen: 0,
      chipsAtHandStart: chips,
    };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    const events: EngineEvent[] = [];
    const inHand = pokerEngine.isHandInProgress!(s) && live(p);
    if (pokerEngine.isHandInProgress!(s) && p.total > 0) s.departed.push({ seat: -1 - s.departed.length, amount: p.total, folded: true });
    s.seats[seat] = null;
    events.push({ type: 'playerLeft', seat });
    if (inHand) {
      events.push({ type: 'action', seat, action: 'fold', to: 0 });
      if (s.turn === seat) {
        const nxt = nextSeatWhere(s.seats, seat, (q) => needsAction(s, q));
        s.turn = nxt ?? s.turn;
      }
      progressIfRoundDone(s, events);
    }
    return { chips: p.chips, events };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    switch (raw.type) {
      case 'fold':
      case 'check':
      case 'call':
      case 'allin':
        return { type: raw.type };
      case 'raise':
      case 'bet':
        if (typeof raw.to !== 'number' || !Number.isInteger(raw.to) || raw.to <= 0) throw new EngineError('INVALID_AMOUNT', 'Invalid raise amount');
        return { type: 'raise', to: raw.to };
      default:
        throw new EngineError('INVALID_ACTION', 'Unknown poker action');
    }
  },

  applyAction(s, seat, action) {
    assertEngine(['PREFLOP', 'FLOP', 'TURN', 'RIVER'].includes(s.phase), 'INVALID_PHASE', 'No betting round in progress');
    assertEngine(s.turn === seat, 'NOT_YOUR_TURN', 'Not your turn');
    const p = s.seats[seat];
    assertEngine(p && canAct(p), 'INVALID_ACTION', 'You cannot act');
    const legal = legalFor(s, seat);
    const events: EngineEvent[] = [];
    switch (action.type) {
      case 'fold':
        p.status = 'FOLDED';
        p.acted = true;
        s.lastAction = { seat, action: 'fold', amount: 0 };
        events.push({ type: 'action', seat, action: 'fold', to: 0 });
        break;
      case 'check':
        assertEngine(legal.canCheck, 'ILLEGAL_MOVE', 'Cannot check facing a bet');
        p.acted = true;
        p.raiseCountSeen = s.fullRaiseCount;
        s.lastAction = { seat, action: 'check', amount: 0 };
        events.push({ type: 'action', seat, action: 'check', to: p.bet });
        break;
      case 'call': {
        assertEngine(legal.call !== null, 'ILLEGAL_MOVE', 'Nothing to call');
        commit(s, seat, legal.call);
        p.acted = true;
        p.raiseCountSeen = s.fullRaiseCount;
        s.lastAction = { seat, action: p.status === 'ALLIN' ? 'allin' : 'call', amount: p.bet };
        events.push({ type: 'action', seat, action: p.status === 'ALLIN' ? 'allin' : 'call', to: p.bet, chips: p.chips, pot: potTotal(s) });
        break;
      }
      case 'raise': {
        assertEngine(legal.minRaiseTo !== null && legal.maxRaiseTo !== null, 'ILLEGAL_MOVE', 'Raising is not allowed now');
        const allIn = action.to === p.bet + p.chips;
        assertEngine(action.to <= legal.maxRaiseTo, 'INVALID_AMOUNT', `Maximum is ${legal.maxRaiseTo}`);
        assertEngine(action.to >= legal.minRaiseTo || (allIn && action.to > s.currentBet), 'INVALID_AMOUNT', `Minimum raise is to ${legal.minRaiseTo}`);
        doRaise(s, seat, action.to, events);
        break;
      }
      case 'allin': {
        const to = p.bet + p.chips;
        if (to <= s.currentBet) {
          commit(s, seat, p.chips);
          p.acted = true;
          p.raiseCountSeen = s.fullRaiseCount;
          s.lastAction = { seat, action: 'allin', amount: p.bet };
          events.push({ type: 'action', seat, action: 'allin', to: p.bet, chips: 0, pot: potTotal(s) });
        } else {
          assertEngine(legal.allInTo !== null, 'ILLEGAL_MOVE', 'All-in not allowed (pot limit or betting closed)');
          doRaise(s, seat, to, events);
        }
        break;
      }
    }
    progressIfRoundDone(s, events);
    return events;
  },

  getActors(s) {
    if (['PREFLOP', 'FLOP', 'TURN', 'RIVER'].includes(s.phase) && s.seats[s.turn] && canAct(s.seats[s.turn]!)) return [s.turn];
    return [];
  },

  getLegalActions: (s, seat) => legalFor(s, seat),

  getTimeoutAction(s, seat) {
    return legalFor(s, seat).canCheck ? { type: 'check' } : { type: 'fold' };
  },

  getView(s, seat) {
    const me = seat === null ? null : s.seats[seat];
    return {
      gameType: 'POKER',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      button: s.button,
      sbSeat: s.sbSeat,
      bbSeat: s.bbSeat,
      turn: s.turn,
      board: [...s.board],
      hand: me ? [...me.hole] : [],
      players: s.seats.map((p, i) =>
        p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, bet: p.bet, total: p.total, hasCards: p.hole.length > 0 && live(p) } : null,
      ),
      pot: potTotal(s),
      currentBet: s.currentBet,
      minRaise: s.minRaise,
      blinds: currentBlinds(s),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
      eliminations: [...s.eliminations],
      lastAction: s.lastAction,
    };
  },

  getSystemStep(s) {
    const ready = seatsWhere(s.seats, eligibleForHand).length >= s.config.minPlayers;
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && ready) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase !== 'HAND_END' && s.phase !== 'WAITING') return events;
    s.phase = 'WAITING';
    const ready = seatsWhere(s.seats, eligibleForHand).length >= Math.max(2, s.config.mode === 'tournament' ? 2 : s.config.minPlayers);
    if (ready) startHand(s, rng, events);
    else events.push({ type: 'waitingForPlayers' });
    return events;
  },

  isFinished: (s) => s.phase === 'FINISHED',
  isHandInProgress: (s) => ['PREFLOP', 'FLOP', 'TURN', 'RIVER'].includes(s.phase),
  getChips: (s) => Object.fromEntries(seatsWhere(s.seats, () => true).map((i) => [i, s.seats[i]!.chips])),

  getStandings(s): Standing[] {
    if (s.config.mode === 'tournament' && s.eliminations.length) {
      return s.eliminations.map((e) => ({ seat: e.seat, place: e.place, score: s.seats[e.seat]?.chips ?? 0 })).sort((a, b) => a.place - b.place);
    }
    const rows = seatsWhere(s.seats, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }));
    rows.sort((a, b) => b.score - a.score);
    return rows.map((r, i) => ({ ...r, place: i + 1 }));
  },

  getRoundsCompleted: (s) => (s.phase === 'HAND_END' || s.phase === 'FINISHED' ? s.handNo : Math.max(0, s.handNo - 1)),
};
